import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getVereinById } from '../../api/vereineApi';
import { getWettkaempferByVerein, addWettkaempfer } from '../../api/wettkaempferApi';
import WettkampferAuswahlModal from '../../components/registration/WettkampferAuswahlModal';
import '../../styles/VereinDetailPage.css';
import '../../components/dashboard/WettkaempferDashboard.css';

// Dropdown-Optionen wie in der DB!
const skillLevels = ["Beginner", "Intermediate", "Advanced", "Black Belt"];

const STAFF_ROLLEN = [
  { value: 'hauptkampfrichter', label: 'Hauptkampfrichter', icon: '👨‍⚖️' },
  { value: 'seitenkampfrichter', label: 'Seitenkampfrichter', icon: '⚖️' },
  { value: 'punktrichter', label: 'Punktrichter', icon: '📝' },
  { value: 'zeitnehmer', label: 'Zeitnehmer', icon: '⏱️' },
  { value: 'mattenleiter', label: 'Mattenleiter', icon: '🎯' },
  { value: 'schreiber', label: 'Schreiber', icon: '✍️' },
  { value: 'helfer', label: 'Helfer', icon: '🤝' },
  { value: 'arzt', label: 'Arzt', icon: '👨‍⚕️' },
  { value: 'sanitaeter', label: 'Sanitäter', icon: '🚑' }
];

const QUALIFIKATIONEN = [
  { value: 'keine', label: 'Keine Lizenz', icon: '⚪' },
  { value: 'c_lizenz', label: 'C-Lizenz', icon: '🟢' },
  { value: 'b_lizenz', label: 'B-Lizenz', icon: '🔵' },
  { value: 'a_lizenz', label: 'A-Lizenz', icon: '🟡' },
  { value: 'international', label: 'International', icon: '🔴' }
];

const gurtfarben = [
  "Weiß", "Weiß/Gelb", "Gelb", "Orange", "Grün", "Grün/Blau", "Blau",
  "Lila", "Blau/Rot", "Rot", "Braun", "Schwarz"
];

function formatDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}

// Preisberechnung für Anmeldungen
function calculatePrice(kategorienCount, preisEinzel, preisTeam, rabattZweite, rabattWeitere, isTeam = false) {
  const basisPreis = isTeam ? (preisTeam || 40) : (preisEinzel || 25);
  let gesamt = 0;

  for (let i = 0; i < kategorienCount; i++) {
    if (i === 0) {
      gesamt += basisPreis;
    } else if (i === 1) {
      gesamt += basisPreis * (1 - (rabattZweite || 20) / 100);
    } else {
      gesamt += basisPreis * (1 - (rabattWeitere || 40) / 100);
    }
  }

  return gesamt;
}

function formatCurrency(amount) {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);
}

function Modal({ open, title, children, onClose }) {
  if (!open) return null;
  return (
    <div className="modal-backdrop vd-modal">
      <div className="modal-content">
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

export default function VereinDetailPage({ vereinId: propVereinId }) {
  const { id: paramId } = useParams();
  const navigate = useNavigate();

  // Use prop vereinId if provided, otherwise fall back to URL param
  const id = propVereinId || paramId;

  const [verein, setVerein] = useState(null);
  const [wettkaempfer, setWettkaempfer] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [modalError, setModalError] = useState('');
  const [saving, setSaving] = useState(false);
  const [vereinDetailsCollapsed, setVereinDetailsCollapsed] = useState(false);
  const [selectedLetter, setSelectedLetter] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editedVerein, setEditedVerein] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingWettkaempfer, setEditingWettkaempfer] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailWettkaempfer, setDetailWettkaempfer] = useState(null);
  const [expandedWettkaempfer, setExpandedWettkaempfer] = useState(new Set());

  // Tab State
  const [activeTab, setActiveTab] = useState('wettkaempfer');

  // Turnier-Anmeldung State
  const [showTurnierModal, setShowTurnierModal] = useState(false);
  const [showTurnierAuswahl, setShowTurnierAuswahl] = useState(false);
  const [verfuegbareTurniere, setVerfuegbareTurniere] = useState([]);
  const [selectedTurnierForAnmeldung, setSelectedTurnierForAnmeldung] = useState(null);
  const [loadingTurniere, setLoadingTurniere] = useState(false);

  // Personal/Staff State
  const [personal, setPersonal] = useState([]);
  const [showAddStaffModal, setShowAddStaffModal] = useState(false);
  const [newStaff, setNewStaff] = useState({
    vorname: '',
    nachname: '',
    email: '',
    telefon: '',
    primaer_rolle: 'helfer',
    qualifikation: 'keine',
    lizenz_nummer: '',
    verband: '',
    notizen: ''
  });

  // Anmeldungen State
  const [anmeldungen, setAnmeldungen] = useState([]);
  const [loadingAnmeldungen, setLoadingAnmeldungen] = useState(false);
  const [editingAnmeldung, setEditingAnmeldung] = useState(null);
  const [expandedAnmeldungWk, setExpandedAnmeldungWk] = useState({}); // { "turnierId-wettkaempferId": true/false }

  // Dojo Sync State
  const [hasDojo, setHasDojo] = useState(false);
  const [, setSyncStatus] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);

  // State für das neue Mitglied
  const [newMember, setNewMember] = useState({
    vorname: "",
    nachname: "",
    geburtsdatum: "",
    gewicht: "",
    skill_level: "",
    email: "",
    handy: "",
    vereins_id: id,
    geschlecht: "",
    nationalitaet: "",
    gurtfarbe: "",
    kampfstil: ""
  });

  const fetchPersonal = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/personal?verein_id=${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setPersonal(data.data);
        }
      }
    } catch (err) {
      console.error('Fehler beim Laden des Personals:', err);
      setPersonal([]);
    }
  };

  const fetchAnmeldungen = async () => {
    if (!id) return;
    setLoadingAnmeldungen(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/anmeldungen/verein/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        console.log('Anmeldungen API Response:', data);
        // API gibt { success, data: { anmeldungen: [...] } } zurück
        if (data.success && data.data && Array.isArray(data.data.anmeldungen)) {
          setAnmeldungen(data.data.anmeldungen);
        } else if (data.success && Array.isArray(data.data)) {
          setAnmeldungen(data.data);
        } else if (Array.isArray(data)) {
          setAnmeldungen(data);
        } else {
          setAnmeldungen([]);
        }
      } else {
        setAnmeldungen([]);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Anmeldungen:', err);
      setAnmeldungen([]);
    } finally {
      setLoadingAnmeldungen(false);
    }
  };

  // Anmeldung bearbeiten - öffnet WettkampferAuswahlModal mit vorausgewähltem Wettkämpfer
  const handleEditAnmeldung = async (anmeldung) => {
    // Turnier-Daten für das Modal vorbereiten
    const turnierForEdit = {
      turnier_id: anmeldung.turnier_id,
      name: anmeldung.turnier_name,
      datum: anmeldung.turnier_datum,
      ort: anmeldung.turnier_ort
    };

    // Alle bereits angemeldeten Kategorien für diesen Wettkämpfer im Turnier laden
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/anmeldungen/wettkampfer/${anmeldung.wettkaempfer_id}/turnier/${anmeldung.turnier_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      let existingDivisions = [];
      if (res.ok) {
        const data = await res.json();
        if (data.success && Array.isArray(data.data)) {
          existingDivisions = data.data.map(a => a.division_code).filter(Boolean);
        }
      }

      console.log('📋 Bereits angemeldete Kategorien:', existingDivisions);

      // Speichere welcher Wettkämpfer bearbeitet wird mit allen bereits angemeldeten Kategorien
      setEditingAnmeldung({
        ...anmeldung,
        wettkaempfer_id: anmeldung.wettkaempfer_id,
        existingDivisions: existingDivisions
      });

      setSelectedTurnierForAnmeldung(turnierForEdit);
      setShowTurnierModal(true);
    } catch (err) {
      console.error('Fehler beim Laden der bestehenden Anmeldungen:', err);
      // Trotzdem fortfahren mit nur der aktuellen Division
      setEditingAnmeldung({
        ...anmeldung,
        wettkaempfer_id: anmeldung.wettkaempfer_id,
        existingDivisions: anmeldung.division_code ? [anmeldung.division_code] : []
      });
      setSelectedTurnierForAnmeldung(turnierForEdit);
      setShowTurnierModal(true);
    }
  };

  // Toggle für Wettkämpfer-Kategorien ein-/ausklappen
  // Toggle für Anmeldungen-Wettkämpfer ein-/ausklappen
  const toggleAnmeldungWkExpand = (turnierId, wettkaempferId) => {
    const key = `${turnierId}-${wettkaempferId}`;
    setExpandedAnmeldungWk(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleDeleteAnmeldung = async (anmeldungId) => {
    if (!window.confirm('Anmeldung wirklich löschen?')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/anmeldungen/cancel/${anmeldungId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ grund: 'Manuell gelöscht' })
      });
      if (res.ok) {
        await fetchAnmeldungen();
      } else {
        const err = await res.json();
        alert('Fehler: ' + (err.message || err.error));
      }
    } catch (err) {
      alert('Fehler beim Löschen: ' + err.message);
    }
  };

  useEffect(() => {
    getVereinById(id)
      .then(data => {
        console.log('🏢 Vereinsdaten geladen:', data);
        setVerein(data);
      })
      .catch(e => console.error('Fehler beim Laden der Vereinsdetails:', e));
    getWettkaempferByVerein(id)
      .then(setWettkaempfer)
      .catch(() => setWettkaempfer([]));

    // Fetch Personal
    fetchPersonal();

    // Fetch Anmeldungen
    fetchAnmeldungen();

    // Check for Dojo integration
    checkDojoIntegration();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- soll nur bei id neu laufen, nicht wenn sich die Ladefunktion neu bildet
  }, [id]);

  const checkDojoIntegration = async () => {
    try {
      const response = await fetch(`/api/dojo-sync/check-updates/${id}`);
      const data = await response.json();

      if (data.success && data.has_integration) {
        setHasDojo(true);
        setLastSync(data.last_sync);
        setSyncStatus(data);
      }
    } catch (error) {
      console.error('Error checking dojo integration:', error);
    }
  };

  const handleDojoSync = async () => {
    setSyncing(true);
    setSyncMessage(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/dojo-sync/sync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ verein_id: id })
      });

      const data = await response.json();

      if (data.success) {
        setSyncMessage({
          type: 'success',
          text: `✅ Sync erfolgreich! ${data.imported} neue, ${data.updated} aktualisiert${data.errors > 0 ? `, ${data.errors} Fehler` : ''}`
        });
        setLastSync(data.last_sync);

        // Reload wettkaempfer list
        const list = await getWettkaempferByVerein(id);
        setWettkaempfer(list);

        // Refresh sync status
        checkDojoIntegration();
      } else {
        setSyncMessage({
          type: 'error',
          text: `❌ Sync fehlgeschlagen: ${data.message}`
        });
      }
    } catch (error) {
      setSyncMessage({
        type: 'error',
        text: `❌ Fehler beim Sync: ${error.message}`
      });
    } finally {
      setSyncing(false);

      // Clear message after 5 seconds
      setTimeout(() => setSyncMessage(null), 5000);
    }
  };

  const handleStartEdit = () => {
    setEditedVerein({ ...verein });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditedVerein(null);
    setIsEditing(false);
  };

  const handleSaveVerein = async () => {
    try {
      setSaving(true);
      const { updateVerein } = await import('../../api/vereineApi');
      await updateVerein(id, editedVerein);
      setVerein(editedVerein);
      setIsEditing(false);
      setEditedVerein(null);
    } catch (error) {
      console.error('Fehler beim Speichern der Vereinsdaten:', error);
      alert('Fehler beim Speichern: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      setLogoUploading(true);
      const formData = new FormData();
      formData.append('logo', file);
      const token = localStorage.getItem('token');
      const res = await fetch('/api/upload/logo', {
        method: 'POST',
        body: formData,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!res.ok) throw new Error('Upload fehlgeschlagen');
      const data = await res.json();
      const logoPath = data.data?.logo || data.logo || data.url || data.path;
      setEditedVerein(prev => ({ ...prev, logo: logoPath }));
    } catch (err) {
      alert('Logo-Upload fehlgeschlagen: ' + err.message);
    } finally {
      setLogoUploading(false);
    }
  };

  const handleOpenAddModal = () => {
    setNewMember({
      vorname: "",
      nachname: "",
      geburtsdatum: "",
      gewicht: "",
      skill_level: "",
      email: "",
      handy: "",
      vereins_id: id,
      geschlecht: "",
      nationalitaet: "",
      gurtfarbe: "",
      kampfstil: ""
    });
    setModalError('');
    setShowAddModal(true);
  };

  // Formular absenden
  const handleWettkaempferSubmit = async (e) => {
    e.preventDefault();
    setModalError('');
    setSaving(true);

    // Pflichtfelder prüfen
    if (!newMember.vorname || !newMember.nachname || !newMember.geburtsdatum || !newMember.skill_level || !newMember.geschlecht || !newMember.gurtfarbe) {
      setModalError("❌ Bitte alle Pflichtfelder ausfüllen!");
      setSaving(false);
      return;
    }

    try {
      // Vereinside IMMER mitgeben!
      const saveObj = { ...newMember, vereins_id: id };
      const saved = await addWettkaempfer(saveObj);
      if (saved && (saved.id || saved.wettkaempfer_id)) {
        const list = await getWettkaempferByVerein(id);
        setWettkaempfer(list);
        setShowAddModal(false);
      } else {
        setModalError("⚠️ Speichern fehlgeschlagen.");
      }
    } catch (err) {
      setModalError("⚠️ Fehler beim Speichern: " + (err.message || ""));
    }
    setSaving(false);
  };

  // Alphabetische Filterung
  const getAvailableLetters = () => {
    const letters = new Set();
    wettkaempfer.forEach(wk => {
      const firstLetter = (wk.nachname || '').charAt(0).toUpperCase();
      if (firstLetter >= 'A' && firstLetter <= 'Z') {
        letters.add(firstLetter);
      }
    });
    return Array.from(letters).sort();
  };

  const filteredWettkaempfer = wettkaempfer.filter(wk => {
    // Filter by letter
    const firstLetter = (wk.nachname || '').charAt(0).toUpperCase();
    const letterMatch = selectedLetter === 'ALL' || firstLetter === selectedLetter;

    // Filter by search query
    const searchMatch = searchQuery === '' ||
      `${wk.vorname} ${wk.nachname}`.toLowerCase().includes(searchQuery.toLowerCase());

    return letterMatch && searchMatch;
  });

  const availableLetters = getAvailableLetters();

  if (!verein) {
    return (
      <div className="verein-detail-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Lade Vereinsdaten...</p>
        </div>
      </div>
    );
  }


  // ===== WETTKÄMPFER ACTION HANDLERS =====
  const handleEditWettkaempfer = (wk) => {
    const formattedDate = wk.geburtsdatum ? wk.geburtsdatum.split('T')[0] : '';
    setEditingWettkaempfer({...wk, geburtsdatum: formattedDate});
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editingWettkaempfer) return;
    setSaving(true);
    setModalError('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/wettkaempfer/${editingWettkaempfer.wettkaempfer_id}`, {
        method: 'PUT',
        headers: {'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json'},
        body: JSON.stringify(editingWettkaempfer)
      });
      if (!response.ok) throw new Error((await response.json()).error || 'Fehler');
      setWettkaempfer(await getWettkaempferByVerein(id));
      setShowEditModal(false);
      setEditingWettkaempfer(null);
      alert('✅ Wettkämpfer aktualisiert');
    } catch (err) {
      setModalError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleShowDetail = (wk) => {
    const formattedDate = wk.geburtsdatum ? wk.geburtsdatum.split('T')[0] : '';
    setDetailWettkaempfer({...wk, geburtsdatum: formattedDate});
    setShowDetailModal(true);
  };

  const handleEditFromDetail = () => {
    setEditingWettkaempfer(detailWettkaempfer);
    setShowDetailModal(false);
    setShowEditModal(true);
  };

  const handleFotoUpload = async (e, wkId) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('foto', file);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/wettkaempfer/${wkId}/foto`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (data.success) {
        setDetailWettkaempfer(prev => ({ ...prev, foto: data.foto }));
        setWettkaempfer(await getWettkaempferByVerein(id));
      }
    } catch (err) {
      alert('Fehler beim Upload: ' + err.message);
    }
  };

  const handleArchiveWettkaempfer = async (wkId) => {
    const grund = prompt('Grund für Archivierung:');
    if (grund === null) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/wettkaempfer/${wkId}/archive`, {
        method: 'POST',
        headers: {'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json'},
        body: JSON.stringify({ grund })
      });
      if (!response.ok) throw new Error((await response.json()).error || 'Fehler');
      setWettkaempfer(await getWettkaempferByVerein(id));
      alert('✅ Archiviert');
    } catch (err) {
      alert('❌ ' + err.message);
    }
  };

  const handleDeleteWettkaempfer = async (wkId) => {
    if (!window.confirm('Wettkämpfer löschen?')) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/wettkaempfer/${wkId}`, {
        method: 'DELETE',
        headers: {'Authorization': `Bearer ${token}`}
      });
      if (!response.ok) throw new Error((await response.json()).error || 'Fehler');
      setWettkaempfer(await getWettkaempferByVerein(id));
      alert('✅ Gelöscht');
    } catch (err) {
      alert('❌ ' + err.message);
    }
  };

  // ===== STAFF/PERSONAL HANDLERS =====
  const handleOpenAddStaffModal = () => {
    setNewStaff({
      vorname: '',
      nachname: '',
      email: '',
      telefon: '',
      primaer_rolle: 'helfer',
      qualifikation: 'keine',
      lizenz_nummer: '',
      verband: '',
      notizen: ''
    });
    setModalError('');
    setShowAddStaffModal(true);
  };

  const handleStaffSubmit = async (e) => {
    e.preventDefault();
    setModalError('');
    setSaving(true);

    if (!newStaff.vorname || !newStaff.nachname) {
      setModalError("❌ Vor- und Nachname sind erforderlich!");
      setSaving(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/personal', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newStaff)
      });

      const data = await res.json();
      if (data.success) {
        await fetchPersonal();
        setShowAddStaffModal(false);
      } else {
        setModalError("⚠️ " + (data.message || 'Fehler beim Speichern'));
      }
    } catch (err) {
      setModalError("⚠️ Fehler: " + err.message);
    }
    setSaving(false);
  };

  const handleDeleteStaff = async (personalId) => {
    if (!window.confirm('Person wirklich deaktivieren?')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/personal/${personalId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        await fetchPersonal();
      }
    } catch (err) {
      alert('❌ ' + err.message);
    }
  };

  const getRolleInfo = (rolle) => STAFF_ROLLEN.find(r => r.value === rolle) || STAFF_ROLLEN[6];
  const getQualInfo = (qual) => QUALIFIKATIONEN.find(q => q.value === qual) || QUALIFIKATIONEN[0];

  // ===== TURNIER-ANMELDUNG FUNKTIONEN =====
  const fetchVerfuegbareTurniere = async () => {
    setLoadingTurniere(true);
    try {
      const token = localStorage.getItem('token');
      const clubDataStr = localStorage.getItem('clubData');
      let authToken = token;

      if (clubDataStr) {
        const clubData = JSON.parse(clubDataStr);
        authToken = clubData.token || token;
      }

      const isAdmin = !!localStorage.getItem('adminData');
      const params = new URLSearchParams(isAdmin ? {} : { zukunft: 'true' });

      const response = await fetch(`/api/turniere?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      let turniereData = [];

      if (data.success && Array.isArray(data.data)) {
        turniereData = data.data;
      } else if (Array.isArray(data)) {
        turniereData = data;
      }

      // Turniere anzeigen: nicht abgesagt, nicht geschlossen
      const offeneTurniere = turniereData.filter(t =>
        !['Abgesagt', 'Geschlossen', 'Abgeschlossen'].includes(t.status)
      );
      setVerfuegbareTurniere(offeneTurniere);

    } catch (error) {
      console.error('Fehler beim Laden der Turniere:', error);
      setVerfuegbareTurniere([]);
    } finally {
      setLoadingTurniere(false);
    }
  };

  const handleOpenTurnierAnmeldung = async () => {
    await fetchVerfuegbareTurniere();
    setShowTurnierAuswahl(true);
  };

  const handleTurnierSelected = (turnier) => {
    setSelectedTurnierForAnmeldung(turnier);
    setShowTurnierAuswahl(false);
    setShowTurnierModal(true);
  };

  const handleAnmeldungAbgeschlossen = (anmeldungData) => {
    setShowTurnierModal(false);
    setSelectedTurnierForAnmeldung(null);
    alert(`✅ Anmeldung erfolgreich!\n\n${anmeldungData.wettkampfer?.length || 0} Wettkämpfer wurden für das Turnier "${anmeldungData.turnier?.name}" angemeldet.`);
  };

  // Toggle Wettkämpfer Card
  const toggleWettkaempferCard = (wkId) => {
    setExpandedWettkaempfer(prev => {
      const newSet = new Set(prev);
      if (newSet.has(wkId)) {
        newSet.delete(wkId);
      } else {
        newSet.add(wkId);
      }
      return newSet;
    });
  };

  return (
    <div className="verein-detail-page">
      {/* Main Content Layout */}
      <div className={`detail-layout ${vereinDetailsCollapsed ? 'sidebar-collapsed' : ''}`}>
        {/* Left Sidebar - Vereinsdaten */}
        <div className={`verein-sidebar ${vereinDetailsCollapsed ? 'collapsed' : ''}`}>
          <div className="sidebar-header">
            <div className="sidebar-header-left" onClick={() => setVereinDetailsCollapsed(!vereinDetailsCollapsed)}>
              <span className="toggle-icon">{vereinDetailsCollapsed ? '▶' : '◀'}</span>
              <h2>Vereinsdaten</h2>
            </div>
            {!vereinDetailsCollapsed && !isEditing && (
              <button className="edit-button" onClick={handleStartEdit}>
                ✏️ Bearbeiten
              </button>
            )}
            {!vereinDetailsCollapsed && isEditing && (
              <div className="edit-actions">
                <button className="save-button" onClick={handleSaveVerein} disabled={saving}>
                  {saving ? '💾 Speichern...' : '💾 Speichern'}
                </button>
                <button className="cancel-button" onClick={handleCancelEdit} disabled={saving}>
                  ❌ Abbrechen
                </button>
              </div>
            )}
          </div>

          {!vereinDetailsCollapsed && (
            <div className="sidebar-content">
              <div className="info-section">
                <h3>Allgemeines</h3>
                <div className="info-item">
                  <span className="info-label">Name:</span>
                  {isEditing ? (
                    <input
                      type="text"
                      className="info-input"
                      value={editedVerein?.name || ''}
                      onChange={(e) => setEditedVerein({...editedVerein, name: e.target.value})}
                    />
                  ) : (
                    <span className="info-value">{verein.name}</span>
                  )}
                </div>
                <div className="info-item">
                  <span className="info-label">Ansprechpartner:</span>
                  {isEditing ? (
                    <input
                      type="text"
                      className="info-input"
                      value={editedVerein?.ansprechpartner || ''}
                      onChange={(e) => setEditedVerein({...editedVerein, ansprechpartner: e.target.value})}
                    />
                  ) : (
                    <span className="info-value">{verein.ansprechpartner}</span>
                  )}
                </div>
              </div>

              <div className="info-section">
                <h3>Kontakt</h3>
                <div className="info-item">
                  <span className="info-label">📧 Email:</span>
                  {isEditing ? (
                    <input
                      type="email"
                      className="info-input"
                      value={editedVerein?.email || ''}
                      onChange={(e) => setEditedVerein({...editedVerein, email: e.target.value})}
                    />
                  ) : (
                    <span className="info-value">{verein.email}</span>
                  )}
                </div>
                <div className="info-item">
                  <span className="info-label">📞 Telefon:</span>
                  {isEditing ? (
                    <input
                      type="tel"
                      className="info-input"
                      value={editedVerein?.telefon || ''}
                      onChange={(e) => setEditedVerein({...editedVerein, telefon: e.target.value})}
                    />
                  ) : (
                    <span className="info-value">{verein.telefon}</span>
                  )}
                </div>
              </div>

              <div className="info-section">
                <h3>Adresse</h3>
                <div className="info-item">
                  <span className="info-label">Straße:</span>
                  {isEditing ? (
                    <input
                      type="text"
                      className="info-input"
                      value={editedVerein?.strasse || ''}
                      onChange={(e) => setEditedVerein({...editedVerein, strasse: e.target.value})}
                      placeholder="Straße"
                    />
                  ) : (
                    <span className="info-value">{verein.strasse} {verein.hausnummer}</span>
                  )}
                </div>
                {isEditing && (
                  <div className="info-item">
                    <span className="info-label">Hausnummer:</span>
                    <input
                      type="text"
                      className="info-input"
                      value={editedVerein?.hausnummer || ''}
                      onChange={(e) => setEditedVerein({...editedVerein, hausnummer: e.target.value})}
                      placeholder="Nr."
                    />
                  </div>
                )}
                <div className="info-item">
                  <span className="info-label">PLZ:</span>
                  {isEditing ? (
                    <input
                      type="text"
                      className="info-input"
                      value={editedVerein?.plz || ''}
                      onChange={(e) => setEditedVerein({...editedVerein, plz: e.target.value})}
                      placeholder="PLZ"
                    />
                  ) : (
                    <span className="info-value">{verein.plz}</span>
                  )}
                </div>
                <div className="info-item">
                  <span className="info-label">Ort:</span>
                  {isEditing ? (
                    <input
                      type="text"
                      className="info-input"
                      value={editedVerein?.ort || ''}
                      onChange={(e) => setEditedVerein({...editedVerein, ort: e.target.value})}
                      placeholder="Ort"
                    />
                  ) : (
                    <span className="info-value">{verein.ort}</span>
                  )}
                </div>
              </div>

              <div className="info-section">
                <h3>Sonstiges</h3>
                <div className="info-item">
                  <span className="info-label">Benutzername:</span>
                  <span className="info-value">{verein.benutzername}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Homepage:</span>
                  {isEditing ? (
                    <input
                      type="url"
                      className="info-input"
                      value={editedVerein?.homepage || ''}
                      onChange={(e) => setEditedVerein({...editedVerein, homepage: e.target.value})}
                      placeholder="https://..."
                    />
                  ) : verein.homepage ? (
                    <span className="info-value">
                      <a href={verein.homepage} target="_blank" rel="noopener noreferrer">
                        {verein.homepage}
                      </a>
                    </span>
                  ) : (
                    <span className="info-value">-</span>
                  )}
                </div>
                <div className="info-item">
                  <span className="info-label">Logo:</span>
                  {isEditing ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {(editedVerein?.logo || verein.logo) && (
                        <img
                          src={editedVerein?.logo || verein.logo}
                          alt="Logo Vorschau"
                          className="verein-logo"
                          style={{ marginBottom: '4px' }}
                        />
                      )}
                      <label style={{
                        display: 'inline-block',
                        background: '#1565C0',
                        color: '#fff',
                        padding: '7px 14px',
                        borderRadius: '6px',
                        cursor: logoUploading ? 'wait' : 'pointer',
                        fontSize: '13px',
                        fontWeight: 600,
                        opacity: logoUploading ? 0.7 : 1
                      }}>
                        {logoUploading ? 'Lädt hoch...' : '📷 Logo hochladen'}
                        <input
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={handleLogoUpload}
                          disabled={logoUploading}
                        />
                      </label>
                      {editedVerein?.logo && (
                        <button
                          type="button"
                          onClick={() => setEditedVerein(prev => ({ ...prev, logo: null }))}
                          style={{
                            background: 'none', border: '1px solid #e53935', color: '#e53935',
                            padding: '5px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px'
                          }}
                        >
                          Logo entfernen
                        </button>
                      )}
                    </div>
                  ) : verein.logo ? (
                    <img src={verein.logo} alt="Logo" className="verein-logo" />
                  ) : (
                    <span className="info-value" style={{ color: '#888', fontStyle: 'italic' }}>Kein Logo hinterlegt</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Content - Wettkämpfer & Personal */}
        <div className="wettkaempfer-content">
          {/* Tab Navigation */}
          <div className="content-tabs">
            <button
              className={`content-tab ${activeTab === 'wettkaempfer' ? 'active' : ''}`}
              onClick={() => setActiveTab('wettkaempfer')}
            >
              🥋 Wettkämpfer ({wettkaempfer.length})
            </button>
            <button
              className={`content-tab ${activeTab === 'personal' ? 'active' : ''}`}
              onClick={() => setActiveTab('personal')}
            >
              👨‍⚖️ Helfer & Kampfrichter ({personal.length})
            </button>
            <button
              className={`content-tab ${activeTab === 'anmeldungen' ? 'active' : ''}`}
              onClick={() => setActiveTab('anmeldungen')}
            >
              📋 Anmeldungen ({anmeldungen.length})
            </button>
            <button className="back-button-tab" onClick={() => navigate(-1)} title="Zurück">
              ← Zurück
            </button>
          </div>

          {/* Actions Bar */}
          <div className="actions-bar">
            <h2>
              {activeTab === 'wettkaempfer' && `Wettkämpfer (${wettkaempfer.length})`}
              {activeTab === 'personal' && `Helfer & Kampfrichter (${personal.length})`}
              {activeTab === 'anmeldungen' && `Turnier-Anmeldungen (${anmeldungen.length})`}
            </h2>
            <div className="actions-buttons">
              {activeTab === 'wettkaempfer' && (
                <>
                  <button className="turnier-button" onClick={handleOpenTurnierAnmeldung}>
                    🏆 Für Turnier anmelden
                  </button>
                  <button className="add-button" onClick={handleOpenAddModal}>
                    ➕ Wettkämpfer hinzufügen
                  </button>
                </>
              )}
              {activeTab === 'personal' && (
                <button className="add-button" onClick={handleOpenAddStaffModal}>
                  ➕ Personal hinzufügen
                </button>
              )}
              {activeTab === 'anmeldungen' && (
                <button className="turnier-button" onClick={handleOpenTurnierAnmeldung}>
                  🏆 Neue Anmeldung
                </button>
              )}
            </div>
          </div>

          {/* ===== WETTKÄMPFER TAB ===== */}
          {activeTab === 'wettkaempfer' && (
            <>
          {/* Dojo Sync Widget - Compact One-Line Layout */}
          {hasDojo && (
            <div className="dojo-sync-widget-compact">
              <div className="sync-widget-title">
                <span className="sync-icon">🔄</span>
                <span>Dojosoftware Synchronisation</span>
              </div>
              <button
                className="sync-button-compact"
                onClick={handleDojoSync}
                disabled={syncing}
              >
                {syncing ? (
                  <>
                    <span className="loading-spinner-small"></span>
                    Synchronisiere...
                  </>
                ) : (
                  '🔄 Jetzt synchronisieren'
                )}
              </button>
              <div className="sync-last-info">
                <span className="info-label">Letzter Sync:</span>
                <span className="info-value">
                  {lastSync ? new Date(lastSync).toLocaleString('de-DE', {
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  }) : 'Noch nicht synchronisiert'}
                </span>
              </div>
            </div>
          )}

          {syncMessage && (
            <div className={`sync-message ${syncMessage.type}`}>
              {syncMessage.text}
            </div>
          )}

          {/* Filter Bar - Search + Alphabetical */}
          <div className="filter-bar">
            <input
              type="text"
              className="search-input"
              placeholder="🔍 Suche nach Namen..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {availableLetters.length > 0 && (
              <div className="letter-filter-inline">
                <button
                  className={`letter-button ${selectedLetter === 'ALL' ? 'active' : ''}`}
                  onClick={() => setSelectedLetter('ALL')}
                >
                  Alle
                </button>
                {availableLetters.map(letter => (
                  <button
                    key={letter}
                    className={`letter-button ${selectedLetter === letter ? 'active' : ''}`}
                    onClick={() => setSelectedLetter(letter)}
                  >
                    {letter}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Wettkämpfer Liste */}
          <div className="wettkaempfer-list">
            {filteredWettkaempfer.length === 0 ? (
              <div className="no-results">
                <p>
                  {selectedLetter === 'ALL'
                    ? 'Noch keine Wettkämpfer angelegt.'
                    : `Keine Wettkämpfer mit Anfangsbuchstabe "${selectedLetter}".`}
                </p>
              </div>
            ) : (
              <div className="wettkaempfer-grid">
                {filteredWettkaempfer.map((wk) => {
                  const wkId = wk.wettkaempfer_id || wk.id;
                  const isExpanded = expandedWettkaempfer.has(wkId);
                  return (
                    <div key={wkId} className={`wettkaempfer-card ${isExpanded ? 'expanded' : 'collapsed'}`}>
                      <div
                        className="wettkaempfer-header clickable"
                        onClick={() => toggleWettkaempferCard(wkId)}
                      >
                        <div className="header-left">
                          <span className="toggle-icon">{isExpanded ? '▼' : '▶'}</span>
                          <h3>{wk.vorname} {wk.nachname}</h3>
                        </div>
                        <div className="header-right">
                          {!isExpanded && wk.geburtsdatum && (
                            <span className="compact-info">🎂 {formatDate(wk.geburtsdatum)}</span>
                          )}
                        </div>
                      </div>
                      {isExpanded && (
                        <>
                          <div className="wettkaempfer-info">
                            {wk.gurtfarbe && (
                              <div className="info-row-dual">
                                <div className="info-item">
                                  <span className="icon">🥋</span>
                                  <span>{wk.gurtfarbe}</span>
                                </div>
                              </div>
                            )}
                            <div className="info-row-dual">
                              {wk.geburtsdatum && (
                                <div className="info-item">
                                  <span className="icon">🎂</span>
                                  <span>{formatDate(wk.geburtsdatum)}</span>
                                </div>
                              )}
                              {wk.geschlecht && (
                                <div className="info-item">
                                  <span className="icon">👤</span>
                                  <span>{wk.geschlecht}</span>
                                </div>
                              )}
                            </div>
                            {wk.gewicht && (
                              <div className="info-row-dual">
                                <div className="info-item">
                                  <span className="icon">⚖️</span>
                                  <span>{wk.gewicht} kg</span>
                                </div>
                                {wk.skill_level && (
                                  <div className="info-item">
                                    <span className="icon">🎯</span>
                                    <span>{wk.skill_level}</span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="wettkaempfer-actions">
                            <button className="action-btn detail-btn" onClick={(e) => { e.stopPropagation(); handleShowDetail(wk); }}>
                              Details
                            </button>
                            <button className="action-btn edit-btn" onClick={(e) => { e.stopPropagation(); handleEditWettkaempfer(wk); }}>
                              Bearbeiten
                            </button>
                            <button className="action-btn archive-btn" onClick={(e) => { e.stopPropagation(); handleArchiveWettkaempfer(wkId); }}>
                              Archiv
                            </button>
                            <button className="action-btn delete-btn" onClick={(e) => { e.stopPropagation(); handleDeleteWettkaempfer(wkId); }}>
                              Löschen
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
            </>
          )}

          {/* ===== PERSONAL TAB ===== */}
          {activeTab === 'personal' && (
            <div className="personal-list">
              {personal.length === 0 ? (
                <div className="no-results">
                  <p>Noch kein Personal angelegt. Fügen Sie Kampfrichter und Helfer hinzu.</p>
                </div>
              ) : (
                <div className="wettkaempfer-grid">
                  {personal.map((p) => {
                    const rolleInfo = getRolleInfo(p.primaer_rolle);
                    const qualInfo = getQualInfo(p.qualifikation);
                    return (
                      <div key={p.personal_id} className="wettkaempfer-card personal-card">
                        <div className="wettkaempfer-header">
                          <h3>{p.vorname} {p.nachname}</h3>
                          <span className="rolle-badge" style={{ background: '#8B0000' }}>
                            {rolleInfo.icon} {rolleInfo.label}
                          </span>
                        </div>
                        <div className="wettkaempfer-info">
                          <div className="info-row-dual">
                            <div className="info-item">
                              <span className="icon">{qualInfo.icon}</span>
                              <span>{qualInfo.label}</span>
                            </div>
                            {p.lizenz_nummer && (
                              <div className="info-item">
                                <span className="icon">🎫</span>
                                <span>{p.lizenz_nummer}</span>
                              </div>
                            )}
                          </div>
                          {p.email && (
                            <div className="info-row-dual">
                              <div className="info-item">
                                <span className="icon">📧</span>
                                <span>{p.email}</span>
                              </div>
                            </div>
                          )}
                          {p.telefon && (
                            <div className="info-row-dual">
                              <div className="info-item">
                                <span className="icon">📞</span>
                                <span>{p.telefon}</span>
                              </div>
                            </div>
                          )}
                          {p.anzahl_turniere > 0 && (
                            <div className="info-row-dual">
                              <div className="info-item">
                                <span className="icon">🏆</span>
                                <span>{p.anzahl_turniere} Turnier-Einsätze</span>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="wettkaempfer-actions">
                          <button className="action-btn delete-btn" onClick={() => handleDeleteStaff(p.personal_id)} title="Deaktivieren">
                            🗑️
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ===== ANMELDUNGEN TAB ===== */}
          {activeTab === 'anmeldungen' && (
            <div className="anmeldungen-list">
              {loadingAnmeldungen ? (
                <div className="loading-container" style={{ padding: '2rem', textAlign: 'center' }}>
                  <div className="loading-spinner"></div>
                  <p>Anmeldungen werden geladen...</p>
                </div>
              ) : !Array.isArray(anmeldungen) || anmeldungen.length === 0 ? (
                <div className="no-results">
                  <p>Noch keine Turnier-Anmeldungen vorhanden.</p>
                  <button className="turnier-button" onClick={handleOpenTurnierAnmeldung} style={{ marginTop: '1rem' }}>
                    🏆 Jetzt für Turnier anmelden
                  </button>
                </div>
              ) : (
                <div className="anmeldungen-grid">
                  {/* Gruppiere Anmeldungen nach Turnier, dann nach Wettkämpfer */}
                  {Object.entries(
                    anmeldungen.reduce((acc, anm) => {
                      const key = anm.turnier_id || 'unknown';
                      if (!acc[key]) {
                        acc[key] = {
                          turnier_id: anm.turnier_id,
                          turnier_name: anm.turnier_name || 'Unbekanntes Turnier',
                          turnier_datum: anm.turnier_datum,
                          turnier_ort: anm.turnier_ort,
                          // Preisfelder
                          preis_einzel: parseFloat(anm.preis_einzel) || 25,
                          preis_team: parseFloat(anm.preis_team) || 40,
                          rabatt_zweite_kategorie: parseFloat(anm.rabatt_zweite_kategorie) || 20,
                          rabatt_weitere_kategorien: parseFloat(anm.rabatt_weitere_kategorien) || 40,
                          wettkaempfer: {}
                        };
                      }
                      // Gruppiere nach Wettkämpfer
                      const wkKey = anm.wettkaempfer_id;
                      if (!acc[key].wettkaempfer[wkKey]) {
                        acc[key].wettkaempfer[wkKey] = {
                          wettkaempfer_id: anm.wettkaempfer_id,
                          vorname: anm.vorname,
                          nachname: anm.nachname,
                          anmeldedatum: anm.anmeldedatum,
                          kategorien: []
                        };
                      }
                      acc[key].wettkaempfer[wkKey].kategorien.push({
                        anmeldung_id: anm.anmeldung_id,
                        division_code: anm.division_code,
                        status: anm.status
                      });
                      return acc;
                    }, {})
                  ).map(([turnierId, turnierData]) => {
                    const wettkaempferList = Object.values(turnierData.wettkaempfer);
                    const totalKategorien = wettkaempferList.reduce((sum, wk) => sum + wk.kategorien.length, 0);

                    // Berechne Gesamtpreis für alle Wettkämpfer
                    const gesamtPreis = wettkaempferList.reduce((sum, wk) => {
                      return sum + calculatePrice(
                        wk.kategorien.length,
                        turnierData.preis_einzel,
                        turnierData.preis_team,
                        turnierData.rabatt_zweite_kategorie,
                        turnierData.rabatt_weitere_kategorien
                      );
                    }, 0);

                    return (
                    <div key={turnierId} className="anmeldung-turnier-card">
                      <div className="anmeldung-turnier-header">
                        <div className="turnier-info">
                          <h3>🏆 {turnierData.turnier_name}</h3>
                          <div className="turnier-details">
                            {turnierData.turnier_datum && (
                              <span>📅 {formatDate(turnierData.turnier_datum)}</span>
                            )}
                            {turnierData.turnier_ort && (
                              <span>📍 {turnierData.turnier_ort}</span>
                            )}
                          </div>
                        </div>
                        <div className="anmeldung-count">
                          <span className="count-badge">{wettkaempferList.length}</span>
                          <span className="count-label">Teilnehmer</span>
                          <span className="count-badge" style={{ marginLeft: '0.5rem', background: 'rgba(168, 85, 247, 0.2)', borderColor: 'rgba(168, 85, 247, 0.4)' }}>{totalKategorien}</span>
                          <span className="count-label">Starts</span>
                          <span className="count-badge" style={{ marginLeft: '0.5rem', background: 'rgba(46, 213, 115, 0.2)', borderColor: 'rgba(46, 213, 115, 0.4)', color: '#2ed573' }}>{formatCurrency(gesamtPreis)}</span>
                        </div>
                      </div>
                      <div className="anmeldung-teilnehmer">
                        {wettkaempferList.map((wk) => {
                          const isExpanded = expandedAnmeldungWk[`${turnierId}-${wk.wettkaempfer_id}`];
                          const wkPreis = calculatePrice(
                            wk.kategorien.length,
                            turnierData.preis_einzel,
                            turnierData.preis_team,
                            turnierData.rabatt_zweite_kategorie,
                            turnierData.rabatt_weitere_kategorien
                          );
                          return (
                            <div key={wk.wettkaempfer_id} className="wettkaempfer-anmeldung-row">
                              <div
                                className="wettkaempfer-header-row"
                                onClick={() => toggleAnmeldungWkExpand(turnierId, wk.wettkaempfer_id)}
                                style={{ cursor: 'pointer' }}
                              >
                                <div className="expand-icon">
                                  {isExpanded ? '▼' : '▶'}
                                </div>
                                <div className="wettkaempfer-name">
                                  <strong>{wk.vorname} {wk.nachname}</strong>
                                </div>
                                <div className="kategorien-badges">
                                  {wk.kategorien.map((kat, idx) => (
                                    <span key={idx} className="kategorie-badge" style={!kat.division_code ? { borderColor: 'rgba(251,191,36,0.4)', color: '#fbbf24' } : {}}>
                                      {kat.division_code || '⚠️ Keine Kategorie'}
                                    </span>
                                  ))}
                                </div>
                                <div className="wettkaempfer-meta">
                                  {(() => {
                                    const mitKat = wk.kategorien.filter(k => k.division_code).length;
                                    const ohneKat = wk.kategorien.filter(k => !k.division_code).length;
                                    return (
                                      <span className="kategorien-count">
                                        {mitKat > 0 && `${mitKat} ${mitKat === 1 ? 'Kategorie' : 'Kategorien'}`}
                                        {mitKat > 0 && ohneKat > 0 && ' · '}
                                        {ohneKat > 0 && <span style={{ color: '#fbbf24' }}>⚠️ {ohneKat} ohne Kategorie</span>}
                                      </span>
                                    );
                                  })()}
                                  <span className="wettkaempfer-preis" style={{ color: '#2ed573', fontWeight: '600' }}>{formatCurrency(wkPreis)}</span>
                                </div>
                                <div className="wettkaempfer-actions">
                                  {wk.kategorien.some(k => !k.division_code) && (
                                    <button
                                      className="action-btn"
                                      onClick={(e) => { e.stopPropagation(); handleEditAnmeldung({ ...wk.kategorien[0], wettkaempfer_id: wk.wettkaempfer_id, vorname: wk.vorname, nachname: wk.nachname, turnier_id: turnierData.turnier_id, turnier_name: turnierData.turnier_name, turnier_datum: turnierData.turnier_datum, turnier_ort: turnierData.turnier_ort }); }}
                                      title="Kategorie nachtragen"
                                      style={{ background: '#1a1a1a', border: '1px solid rgba(251,191,36,0.4)', color: '#fbbf24', padding: '0.4rem 0.75rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600' }}
                                    >
                                      + Kategorie
                                    </button>
                                  )}
                                  <button
                                    className="action-btn edit-btn"
                                    onClick={(e) => { e.stopPropagation(); handleEditAnmeldung({ ...wk.kategorien[0], wettkaempfer_id: wk.wettkaempfer_id, vorname: wk.vorname, nachname: wk.nachname, turnier_id: turnierData.turnier_id, turnier_name: turnierData.turnier_name, turnier_datum: turnierData.turnier_datum, turnier_ort: turnierData.turnier_ort }); }}
                                    title="Kategorien bearbeiten"
                                    style={{ background: '#1a1a1a', border: '1px solid rgba(168, 85, 247, 0.3)', color: 'white', padding: '0.4rem 0.6rem', borderRadius: '6px', cursor: 'pointer' }}
                                  >
                                    ✏️
                                  </button>
                                </div>
                              </div>
                              {isExpanded && (
                                <div className="kategorien-details">
                                  <table className="kategorien-table">
                                    <thead>
                                      <tr>
                                        <th>Kategorie</th>
                                        <th>Status</th>
                                        <th>Aktion</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {wk.kategorien.map((kat, idx) => (
                                        <tr key={idx}>
                                          <td>{kat.division_code || '-'}</td>
                                          <td>
                                            <span className={`status-badge status-${(kat.status || 'angemeldet').toLowerCase()}`}>
                                              {kat.status === 'Angemeldet' && '✅ '}
                                              {kat.status === 'Storniert' && '❌ '}
                                              {kat.status === 'Bestätigt' && '✔️ '}
                                              {kat.status || 'Angemeldet'}
                                            </span>
                                          </td>
                                          <td>
                                            <button
                                              className="action-btn delete-btn"
                                              onClick={() => handleDeleteAnmeldung(kat.anmeldung_id)}
                                              title="Stornieren"
                                              style={{ background: '#1a1a1a', border: '1px solid rgba(239, 68, 68, 0.3)', color: 'white', padding: '0.3rem 0.5rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}
                                            >
                                              🗑️
                                            </button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );})}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal für neues Mitglied */}
      <Modal open={showAddModal} title="Wettkämpfer anlegen" onClose={() => setShowAddModal(false)}>
        <form onSubmit={handleWettkaempferSubmit} className="wettkaempfer-form">
          <div className="form-grid">
            <div className="form-column">
              <label>
                Vorname *
                <input
                  type="text"
                  value={newMember.vorname}
                  onChange={e => setNewMember({...newMember, vorname: e.target.value})}
                  required
                />
              </label>
              <label>
                Nachname *
                <input
                  type="text"
                  value={newMember.nachname}
                  onChange={e => setNewMember({...newMember, nachname: e.target.value})}
                  required
                />
              </label>
              <label>
                Geburtsdatum *
                <input
                  type="date"
                  value={newMember.geburtsdatum}
                  onChange={e => setNewMember({...newMember, geburtsdatum: e.target.value})}
                  required
                />
              </label>
              <label>
                Gewicht (kg)
                <input
                  type="number"
                  value={newMember.gewicht}
                  onChange={e => setNewMember({...newMember, gewicht: e.target.value})}
                  min="0"
                  step="0.1"
                />
              </label>
              <label>
                Skill Level *
                <select
                  value={newMember.skill_level}
                  onChange={e => setNewMember({...newMember, skill_level: e.target.value})}
                  required
                >
                  <option value="">Bitte wählen</option>
                  {skillLevels.map(level => <option key={level} value={level}>{level}</option>)}
                </select>
              </label>
              <label>
                Geschlecht *
                <select
                  value={newMember.geschlecht}
                  onChange={e => setNewMember({...newMember, geschlecht: e.target.value})}
                  required
                >
                  <option value="">Bitte wählen</option>
                  <option value="männlich">männlich</option>
                  <option value="weiblich">weiblich</option>
                </select>
              </label>
            </div>
            <div className="form-column">
              <label>
                Nationalität
                <input
                  type="text"
                  value={newMember.nationalitaet}
                  onChange={e => setNewMember({...newMember, nationalitaet: e.target.value})}
                />
              </label>
              <label>
                Gurtfarbe *
                <select
                  value={newMember.gurtfarbe}
                  onChange={e => setNewMember({...newMember, gurtfarbe: e.target.value})}
                  required
                >
                  <option value="">Bitte wählen</option>
                  {gurtfarben.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </label>
              <label>
                Kampfstil
                <input
                  type="text"
                  value={newMember.kampfstil}
                  onChange={e => setNewMember({...newMember, kampfstil: e.target.value})}
                />
              </label>
              <label>
                E-Mail
                <input
                  type="email"
                  value={newMember.email}
                  onChange={e => setNewMember({...newMember, email: e.target.value})}
                />
              </label>
              <label>
                Handy
                <input
                  type="tel"
                  value={newMember.handy}
                  onChange={e => setNewMember({...newMember, handy: e.target.value})}
                />
              </label>
              <button className="submit-button" type="submit" disabled={saving}>
                {saving ? "Speichern..." : "Speichern"}
              </button>
              {modalError && <div className="error-message">{modalError}</div>}
            </div>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal open={showEditModal} title="Wettkämpfer bearbeiten" onClose={() => setShowEditModal(false)}>
        {editingWettkaempfer && (
          <form onSubmit={handleSaveEdit} className="wettkaempfer-form">
            {modalError && <div className="error-message">{modalError}</div>}
            <div className="form-grid">
              <div className="form-column">
                <label>
                  Vorname *
                  <input type="text" value={editingWettkaempfer.vorname} onChange={e => setEditingWettkaempfer({...editingWettkaempfer, vorname: e.target.value})} required />
                </label>
                <label>
                  Nachname *
                  <input type="text" value={editingWettkaempfer.nachname} onChange={e => setEditingWettkaempfer({...editingWettkaempfer, nachname: e.target.value})} required />
                </label>
                <label>
                  Geburtsdatum *
                  <input type="date" value={editingWettkaempfer.geburtsdatum} onChange={e => setEditingWettkaempfer({...editingWettkaempfer, geburtsdatum: e.target.value})} required />
                </label>
                <label>
                  Geschlecht *
                  <select value={editingWettkaempfer.geschlecht || ''} onChange={e => setEditingWettkaempfer({...editingWettkaempfer, geschlecht: e.target.value})} required>
                    <option value="">Bitte wählen</option>
                    <option value="M">Männlich</option>
                    <option value="W">Weiblich</option>
                  </select>
                </label>
                <label>
                  Gewicht (kg) *
                  <input type="number" step="0.1" value={editingWettkaempfer.gewicht || ''} onChange={e => setEditingWettkaempfer({...editingWettkaempfer, gewicht: e.target.value})} required />
                </label>
                <label>
                  Skill Level *
                  <select value={editingWettkaempfer.skill_level || ''} onChange={e => setEditingWettkaempfer({...editingWettkaempfer, skill_level: e.target.value})} required>
                    <option value="">Bitte wählen</option>
                    {skillLevels.map(level => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="form-column">
                <label>
                  Gurtfarbe
                  <select value={editingWettkaempfer.gurtfarbe || ''} onChange={e => setEditingWettkaempfer({...editingWettkaempfer, gurtfarbe: e.target.value})}>
                    <option value="">Bitte wählen</option>
                    {gurtfarben.map(farbe => (
                      <option key={farbe} value={farbe}>{farbe}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Kampfstil
                  <input type="text" value={editingWettkaempfer.kampfstil || ''} onChange={e => setEditingWettkaempfer({...editingWettkaempfer, kampfstil: e.target.value})} />
                </label>
                <label>
                  Nationalität
                  <input type="text" value={editingWettkaempfer.nationalitaet || 'DE'} onChange={e => setEditingWettkaempfer({...editingWettkaempfer, nationalitaet: e.target.value})} />
                </label>
                <label>
                  E-Mail
                  <input type="email" value={editingWettkaempfer.email || ''} onChange={e => setEditingWettkaempfer({...editingWettkaempfer, email: e.target.value})} />
                </label>
                <label>
                  Handy
                  <input type="tel" value={editingWettkaempfer.handy || ''} onChange={e => setEditingWettkaempfer({...editingWettkaempfer, handy: e.target.value})} />
                </label>
              </div>
            </div>
            <button className="submit-button" type="submit" disabled={saving}>
              {saving ? "Speichere..." : "Änderungen speichern"}
            </button>
          </form>
        )}
      </Modal>

      {/* Detail Modal */}
      <Modal open={showDetailModal} title="Wettkämpfer" onClose={() => setShowDetailModal(false)}>
        {detailWettkaempfer && (
          <div className="detail-view">
            {/* Foto + Name */}
            <div className="detail-photo-area">
              <div className="detail-photo-wrapper">
                {detailWettkaempfer.foto ? (
                  <img src={detailWettkaempfer.foto} alt="Foto" className="detail-photo" />
                ) : (
                  <div className="detail-photo-placeholder">
                    <span>👤</span>
                  </div>
                )}
                <label className="detail-photo-upload-btn" title="Foto hochladen">
                  📷
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => handleFotoUpload(e, detailWettkaempfer.wettkaempfer_id)}
                  />
                </label>
              </div>
              <div className="detail-name-area">
                <h2>{detailWettkaempfer.vorname} {detailWettkaempfer.nachname}</h2>
                {detailWettkaempfer.gurtfarbe && (
                  <span className="detail-gurt-badge">{detailWettkaempfer.gurtfarbe}</span>
                )}
              </div>
            </div>

            {/* Info Grid */}
            <div className="detail-info-grid">
              <div className="detail-info-item">
                <span className="detail-info-label">Geburtsdatum</span>
                <span className="detail-info-value">{formatDate(detailWettkaempfer.geburtsdatum) || '–'}</span>
              </div>
              <div className="detail-info-item">
                <span className="detail-info-label">Geschlecht</span>
                <span className="detail-info-value">{detailWettkaempfer.geschlecht || '–'}</span>
              </div>
              <div className="detail-info-item">
                <span className="detail-info-label">Gewicht</span>
                <span className="detail-info-value">{detailWettkaempfer.gewicht ? `${detailWettkaempfer.gewicht} kg` : '–'}</span>
              </div>
              <div className="detail-info-item">
                <span className="detail-info-label">Skill Level</span>
                <span className="detail-info-value">{detailWettkaempfer.skill_level || '–'}</span>
              </div>
              <div className="detail-info-item">
                <span className="detail-info-label">Nationalität</span>
                <span className="detail-info-value">{detailWettkaempfer.nationalitaet || 'DE'}</span>
              </div>
              <div className="detail-info-item">
                <span className="detail-info-label">Kampfstil</span>
                <span className="detail-info-value">{detailWettkaempfer.kampfstil || '–'}</span>
              </div>
              {detailWettkaempfer.email && (
                <div className="detail-info-item detail-info-full">
                  <span className="detail-info-label">E-Mail</span>
                  <span className="detail-info-value">{detailWettkaempfer.email}</span>
                </div>
              )}
              {detailWettkaempfer.handy && (
                <div className="detail-info-item detail-info-full">
                  <span className="detail-info-label">Handy</span>
                  <span className="detail-info-value">{detailWettkaempfer.handy}</span>
                </div>
              )}
            </div>

            <div className="detail-modal-actions">
              <button className="detail-edit-btn" onClick={handleEditFromDetail}>
                Bearbeiten
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal für neues Personal */}
      <Modal open={showAddStaffModal} title="Personal anlegen" onClose={() => setShowAddStaffModal(false)}>
        <form onSubmit={handleStaffSubmit} className="wettkaempfer-form">
          <div className="form-grid">
            <div className="form-column">
              <label>
                Vorname *
                <input
                  type="text"
                  value={newStaff.vorname}
                  onChange={e => setNewStaff({...newStaff, vorname: e.target.value})}
                  required
                />
              </label>
              <label>
                Nachname *
                <input
                  type="text"
                  value={newStaff.nachname}
                  onChange={e => setNewStaff({...newStaff, nachname: e.target.value})}
                  required
                />
              </label>
              <label>
                Rolle *
                <select
                  value={newStaff.primaer_rolle}
                  onChange={e => setNewStaff({...newStaff, primaer_rolle: e.target.value})}
                  required
                >
                  {STAFF_ROLLEN.map(r => (
                    <option key={r.value} value={r.value}>{r.icon} {r.label}</option>
                  ))}
                </select>
              </label>
              <label>
                Qualifikation
                <select
                  value={newStaff.qualifikation}
                  onChange={e => setNewStaff({...newStaff, qualifikation: e.target.value})}
                >
                  {QUALIFIKATIONEN.map(q => (
                    <option key={q.value} value={q.value}>{q.icon} {q.label}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="form-column">
              <label>
                E-Mail
                <input
                  type="email"
                  value={newStaff.email}
                  onChange={e => setNewStaff({...newStaff, email: e.target.value})}
                />
              </label>
              <label>
                Telefon
                <input
                  type="tel"
                  value={newStaff.telefon}
                  onChange={e => setNewStaff({...newStaff, telefon: e.target.value})}
                />
              </label>
              <label>
                Lizenz-Nr.
                <input
                  type="text"
                  value={newStaff.lizenz_nummer}
                  onChange={e => setNewStaff({...newStaff, lizenz_nummer: e.target.value})}
                  placeholder="z.B. DTU-12345"
                />
              </label>
              <label>
                Verband
                <input
                  type="text"
                  value={newStaff.verband}
                  onChange={e => setNewStaff({...newStaff, verband: e.target.value})}
                  placeholder="z.B. DTU, WTF"
                />
              </label>
              <button className="submit-button" type="submit" disabled={saving}>
                {saving ? "Speichern..." : "Speichern"}
              </button>
              {modalError && <div className="error-message">{modalError}</div>}
            </div>
          </div>
        </form>
      </Modal>

      {/* Turnier-Auswahl Modal */}
      <Modal open={showTurnierAuswahl} title="Turnier für Anmeldung wählen" onClose={() => setShowTurnierAuswahl(false)}>
        <div className="turnier-auswahl-content">
          {loadingTurniere ? (
            <div className="loading-container" style={{ padding: '2rem', textAlign: 'center' }}>
              <div className="loading-spinner"></div>
              <p>Turniere werden geladen...</p>
            </div>
          ) : verfuegbareTurniere.length === 0 ? (
            <div className="no-results" style={{ padding: '2rem', textAlign: 'center' }}>
              <h3>Keine verfügbaren Turniere</h3>
              <p>Derzeit sind keine Turniere mit offener Anmeldung verfügbar.</p>
            </div>
          ) : (
            <div className="turnier-auswahl-grid">
              {verfuegbareTurniere.map(turnier => (
                <div
                  key={turnier.turnier_id}
                  className="turnier-auswahl-card"
                  onClick={() => handleTurnierSelected(turnier)}
                >
                  <div className="turnier-auswahl-header">
                    <h3>{turnier.name}</h3>
                    <span className="turnier-datum">
                      {turnier.datum_formatted || formatDate(turnier.datum)}
                    </span>
                  </div>
                  <div className="turnier-auswahl-info">
                    <p>📍 {turnier.ort}</p>
                    <p>🥋 {turnier.disziplin || 'Alle Disziplinen'}</p>
                    {turnier.anmeldeschluss && (
                      <p className="anmeldeschluss">
                        ⏰ Anmeldeschluss: {turnier.anmeldeschluss_formatted || formatDate(turnier.anmeldeschluss)}
                      </p>
                    )}
                  </div>
                  <div className="turnier-auswahl-action">
                    <span>Wettkämpfer anmelden →</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>

      {/* Wettkämpfer-Auswahl Modal für Turnier-Anmeldung */}
      {showTurnierModal && selectedTurnierForAnmeldung && (
        <WettkampferAuswahlModal
          turnier={selectedTurnierForAnmeldung}
          onClose={() => {
            setShowTurnierModal(false);
            setSelectedTurnierForAnmeldung(null);
            setEditingAnmeldung(null);
          }}
          onAnmeldungAbgeschlossen={(data) => {
            handleAnmeldungAbgeschlossen(data);
            setEditingAnmeldung(null);
            fetchAnmeldungen();
          }}
          preselectedVerein={verein ? { vereins_id: verein.vereins_id, name: verein.name } : null}
          editingWettkaempfer={editingAnmeldung ? {
            wettkaempfer_id: editingAnmeldung.wettkaempfer_id,
            division_code: editingAnmeldung.division_code,
            existingDivisions: editingAnmeldung.existingDivisions || []
          } : null}
        />
      )}

    </div>
  );
}
